import { useEffect, useRef } from "react";

const COLORS = ["#ff3b5c", "#ffb800", "#2ee59d", "#2d9cff", "#a855f7", "#ff7a1a", "#ffffff"];
// Bursts fire from both side cannons at these offsets (ms) after mount.
const BURST_TIMES = [0, 350, 700, 1100, 1500, 2000];
const PARTICLES_PER_BURST = 45;
const GRAVITY = 0.18;
const DRAG = 0.985;
const MAX_LIFE_MS = 6000;

const rand = (min, max) => min + Math.random() * (max - min);

// Full-screen confetti from the left and right screen edges.
// Mount it to fire once; unmount to stop.
const Confetti = () => {
    const canvasRef = useRef(null);

    useEffect(() => {
        if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        let width = 0;
        let height = 0;
        let particles = [];
        let raf = 0;

        const resize = () => {
            const dpr = window.devicePixelRatio || 1;
            width = window.innerWidth;
            height = window.innerHeight;
            canvas.width = width * dpr;
            canvas.height = height * dpr;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        };

        // side: 1 = left edge cannon (shoots right), -1 = right edge cannon (shoots left).
        // Drag 0.985 and gravity 0.18 give a horizontal reach of ~50 * vx (vx ~ mean speed 9 * cos ~29 deg) before particles
        // fall away. Speed scales with sqrt(width/1200), floored at 0.55: desktop 1200px lands ~33% across, phone 375px ~57%.
        const burst = (side) => {
            const x = side === 1 ? 0 : width;
            for (let i = 0; i < PARTICLES_PER_BURST; i++) {
                // 10-45 degrees above the horizontal, towards the centre.
                const angle = ((rand(10, 45) * Math.PI) / 180);
                const speed = rand(6, 12) * Math.min(1.2, Math.max(0.55, Math.sqrt(width / 1200)));
                particles.push({
                    x,
                    y: height * rand(0.45, 0.6),
                    vx: Math.cos(angle) * speed * side,
                    vy: -Math.sin(angle) * speed,
                    size: rand(6, 11),
                    color: COLORS[Math.floor(Math.random() * COLORS.length)],
                    circle: Math.random() < 0.35,
                    rotation: rand(0, Math.PI * 2),
                    spin: rand(-0.3, 0.3),
                    wobble: rand(0, Math.PI * 2),
                    wobbleSpeed: rand(0.1, 0.3),
                    born: performance.now()
                });
            }
        };

        const draw = (now) => {
            ctx.clearRect(0, 0, width, height);
            particles = particles.filter(
                (p) => p.y < height + 40 && now - p.born < MAX_LIFE_MS
            );
            particles.forEach((p) => {
                p.vx *= DRAG;
                p.vy = p.vy * DRAG + GRAVITY;
                p.x += p.vx;
                p.y += p.vy;
                p.rotation += p.spin;
                p.wobble += p.wobbleSpeed;
                const fade = Math.min(1, (MAX_LIFE_MS - (now - p.born)) / 1000);
                ctx.save();
                ctx.globalAlpha = fade;
                ctx.translate(p.x, p.y);
                ctx.rotate(p.rotation);
                ctx.fillStyle = p.color;
                if (p.circle) {
                    ctx.beginPath();
                    ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
                    ctx.fill();
                } else {
                    // Flip the rectangle's height to fake a tumbling paper.
                    ctx.fillRect(-p.size / 2, (-p.size / 4) * Math.cos(p.wobble), p.size, (p.size / 2) * Math.cos(p.wobble));
                }
                ctx.restore();
            });
        };

        let lastBurst = BURST_TIMES.length;
        const start = performance.now();
        const loop = (now) => {
            while (lastBurst > 0 && now - start >= BURST_TIMES[BURST_TIMES.length - lastBurst]) {
                lastBurst--;
                burst(1);
                burst(-1);
            }
            draw(now);
            if (lastBurst > 0 || particles.length > 0) {
                raf = requestAnimationFrame(loop);
            } else {
                ctx.clearRect(0, 0, width, height);
            }
        };

        resize();
        window.addEventListener("resize", resize);
        raf = requestAnimationFrame(loop);

        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("resize", resize);
            ctx.clearRect(0, 0, width, height);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            aria-hidden="true"
            style={{
                position: "fixed",
                inset: 0,
                width: "100vw",
                height: "100vh",
                pointerEvents: "none",
                zIndex: 2100
            }}
        />
    );
};

export default Confetti;
